import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Form } from "@sscs/ui";
import {
  CONSENT_TO_ANALYTICS_NAME,
  CONSENT_TO_FUNCTIONAL_COOKIES_NAME,
  getClassName,
} from "@sscs/utils";
import { Dialog } from "Components";
import { CookiePreferences } from "./CookieConsentBanner";
import styles from "./cookieConsentBanner.module.scss";

type CookieConsentDialogProps = {
  isOpen: boolean;
  analyticsUsed: boolean;
  gtmUsed: boolean;
  acceptAllCookies: () => void;
  saveCustomPreferences: (cookiePreferences: CookiePreferences) => void;
  onClose: () => void;
  className?: string;
};

const CookieConsentDialog = ({
  isOpen,
  analyticsUsed,
  gtmUsed,
  acceptAllCookies,
  saveCustomPreferences,
  onClose,
  className,
}: CookieConsentDialogProps) => {
  const formMethods = useForm<{
    analytics: "accept" | "decline";
    functional: "accept" | "decline";
  }>({
    defaultValues: {
      analytics: "decline",
      functional: "accept",
    },
  });
  const areAnalyticsAllowed = formMethods.watch("analytics") === "accept";
  const cls = getClassName(styles["consent-dialog"], className);

  const onSaveCustomPreferences = () => {
    const cookiePreferences: CookiePreferences = {
      [CONSENT_TO_FUNCTIONAL_COOKIES_NAME]: true,
      [CONSENT_TO_ANALYTICS_NAME]: analyticsUsed
        ? areAnalyticsAllowed
        : undefined,
    };

    saveCustomPreferences(cookiePreferences);
  };

  return (
    <Dialog
      title="Manage cookies"
      confirmText="Save custom preferences"
      cancelText="Accept all cookies"
      confirmProps={{ "aria-label": "save custom preferences" }}
      cancelProps={{ "aria-label": "accept all cookies" }}
      onConfirm={onSaveCustomPreferences}
      onCancel={acceptAllCookies}
      isOpen={isOpen}
      onClose={onClose}
      cancelButtonColor="secondary"
      confirmButtonColor="secondary"
      className={cls}
    >
      <FormProvider {...formMethods}>
        <Form.Form>
          <Form.RadioGroup
            name="functional"
            label={
              <>
                <h3>Functional</h3>
                <p className={styles["consent-label-description"]}>
                  These cookies provide improvements to the usability and
                  performance of the website and support for various features
                  (sessionid, csrftoken).
                </p>
              </>
            }
            radioGroupClassName={styles["consent-dialog-radio-group"]}
            items={[
              {
                id: "accept-functional-cookies",
                value: "accept",
                label: "Accept",
                disabled: true,
              },
              {
                id: "decline-functional-cookies",
                value: "decline",
                label: "Decline",
                disabled: true,
              },
            ]}
          />
          {analyticsUsed && (
            <Form.RadioGroup
              name="analytics"
              label={
                <>
                  <h3>Analytics</h3>
                  <p className={styles["consent-label-description"]}>
                    These cookies are used for analysis of Site usage (Google
                    Analytics, HotJar{gtmUsed ? ", PostHog" : ""}).
                  </p>
                </>
              }
              radioGroupClassName={styles["consent-dialog-radio-group"]}
              items={[
                {
                  id: "accept-analytics-cookies",
                  value: "accept",
                  label: "Accept",
                },
                {
                  id: "decline-analytics-cookies",
                  value: "decline",
                  label: "Decline",
                },
              ]}
            />
          )}
        </Form.Form>
      </FormProvider>
    </Dialog>
  );
};

export default CookieConsentDialog;
